/* eslint-disable max-len */
import { toastr } from 'react-redux-toastr';
import {
    pendingTask, // The action key for modifying loading state
    begin, // The action value if a "long" running task begun
    end, // The action value if a "long" running task ended
} from 'react-redux-spinner';
import * as api from '../../services/api-endpoints';
import i18n from '../../../services/i18n';
import { RESET_AWARDING_RULE } from './awardingRule.actions';
import { ADD_AWARDING_RULE_TO_LIST, UPDATE_ADMIN_APP_DATA } from '../appData';
import { LOADING_END, LOADING_START } from '../../../store/loading';

/**
 * @param {AwardingRule.IAwardingRuleBrief} adminAwardingRule
 * @param {(() => void)} callBack
 * @param {boolean} isDuplicating
 */
export const createAwardingRule = (adminAwardingRule, callBack, isDuplicating) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const parsedBody = { ...adminAwardingRule, parameters: adminAwardingRule.parameters.map((x, index) => ({ ...x, orderNumber: index })) };
        const response = await api.createAwardingRule(parsedBody);
        const parsedParameters = response.data.parameters.sort((x) => x.orderNumber).map((p) => ({
            sortOrderType: p.sortOrderType,
            dataviewKeyAlias: p.dataviewKeyAlias,
            dataType: p.dataType,
            customDateFormatId: p.customDateFormatId,
        }));
        const updatedAwardingRuleSet = { ...response.data, parameters: parsedParameters };

        if (response.status === 200) {
            dispatch({
                type: ADD_AWARDING_RULE_TO_LIST,
                payload: updatedAwardingRuleSet,
            });
            toastr.success(
                isDuplicating ? i18n.t('shiftBidding:admin.awardingRules.duplication') : i18n.t('shiftBidding:admin.awardingRules.creation'),
                isDuplicating ? i18n.t('shiftBidding:admin.awardingRules.duplicationSuccess') : i18n.t('shiftBidding:admin.awardingRules.creationSuccess'),
            );
            dispatch({ type: RESET_AWARDING_RULE });
            callBack();
        } else {
            toastr.error(
                isDuplicating ? i18n.t('shiftBidding:admin.awardingRules.duplication') : i18n.t('shiftBidding:admin.awardingRules.creation'),
                isDuplicating ? i18n.t('shiftBidding:admin.awardingRules.duplicationFail') : i18n.t('shiftBidding:admin.awardingRules.creationFail'),
            );
        }
    } catch (error) {
        toastr.error(
            isDuplicating ? i18n.t('shiftBidding:admin.awardingRules.duplication') : i18n.t('shiftBidding:admin.awardingRules.creation'),
            error.response?.data.Message,
        );
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {AwardingRule.IAwardingRuleBrief} adminAwardingRule
 * @param {{awardingRules: AwardingRule.IAwardingRuleBrief[]}} storeData
 * @param {(() => void)} callBack
 */
export const editAwardingRule = (adminAwardingRule, storeData, callBack) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const { awardingRules } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const parsedBody = { ...adminAwardingRule, parameters: adminAwardingRule.parameters.map((x, index) => ({ ...x, orderNumber: index })) };
        const response = await api.updateAwardingRule(adminAwardingRule.ruleSetId, parsedBody);
        if (response.status === 200) {
            const parsedParameters = response.data.parameters.sort((x) => x.orderNumber).map((p) => ({
                sortOrderType: p.sortOrderType,
                dataviewKeyAlias: p.dataviewKeyAlias,
                dataType: p.dataType,
                customDateFormatId: p.customDateFormatId,
            }));
            const updatedAwardingRuleSet = { ...response.data, parameters: parsedParameters };
            const indexToUpdate = awardingRules.findIndex((x) => x.ruleSetId === updatedAwardingRuleSet.ruleSetId);
            awardingRules[indexToUpdate] = {
                ...updatedAwardingRuleSet,
            };

            dispatch({
                type: UPDATE_ADMIN_APP_DATA,
                payload: { awardingRules },
            });
            toastr.success(i18n.t('shiftBidding:admin.awardingRules.edition'), i18n.t('shiftBidding:admin.awardingRules.editionSuccess'));
            dispatch({ type: RESET_AWARDING_RULE });
            callBack();
        } else {
            toastr.error(i18n.t('shiftBidding:admin.awardingRules.edition'), i18n.t('shiftBidding:admin.awardingRules.editionFail'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:admin.awardingRules.edition'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};

/**
 * @param {number} adminAwardingRuleId
 * @param {{awardingRules: AwardingRule.IAwardingRuleBrief[]}} storeData
 */
export const deleteAwardingRule = (adminAwardingRuleId, storeData) => /** @param {import('redux').Dispatch} dispatch */async (dispatch) => {
    const { awardingRules } = storeData;
    try {
        dispatch({ type: LOADING_START, [pendingTask]: begin });
        const response = await api.deleteAwardingRule(adminAwardingRuleId);
        if (response.status === 200) {
            const indexToRemove = awardingRules.findIndex((x) => x.ruleSetId === adminAwardingRuleId);
            awardingRules.splice(indexToRemove, 1);
            dispatch({
                type: UPDATE_ADMIN_APP_DATA,
                payload: { awardingRules },
            });
            toastr.success(i18n.t('shiftBidding:admin.awardingRules.deletion'), i18n.t('shiftBidding:admin.awardingRules.deletionSuccess'));
        } else {
            toastr.success(i18n.t('shiftBidding:admin.awardingRules.deletion'), i18n.t('shiftBidding:admin.awardingRules.deletionFail'));
        }
    } catch (error) {
        toastr.error(i18n.t('shiftBidding:admin.awardingRules.deletion'), error.response.data.Message);
    } finally {
        dispatch({ type: LOADING_END, [pendingTask]: end });
    }
};
